import type { ChatAppRequestOverrides } from "@/api/models"

export type PluginRequestOverrides = Pick<ChatAppRequestOverrides, "use_plugins" | "plugin_ids">

/**
 * Translate the footer menu's plugin state into request overrides.
 *
 * - Plugins not offered on this route/stamp: send nothing, so the backend keeps
 *   its default (attach the plugin tools whenever the tenant enabled the store)
 *   and non-Explorer callers are unaffected.
 * - Master switch off: `use_plugins: false` strips the plugin tools this turn.
 * - Some installed plugins switched off: `plugin_ids` carries the remaining
 *   allowlist. It only ever narrows; the backend still authorizes each id.
 */
export type PluginRequestState = {
  /** The Plugins submenu is offered on this route/stamp. */
  available: boolean
  /** Per-chat master switch. */
  enabled: boolean
  installedPluginIds: readonly string[]
  disabledPluginIds: readonly string[]
}

export function buildPluginRequestOverrides({
  available,
  enabled,
  installedPluginIds,
  disabledPluginIds,
}: PluginRequestState): PluginRequestOverrides {
  if (!available) return {}
  if (!enabled) return { use_plugins: false }
  if (disabledPluginIds.length === 0) return { use_plugins: true }
  const disabled = new Set(disabledPluginIds)
  return {
    use_plugins: true,
    plugin_ids: installedPluginIds.filter((pluginId) => !disabled.has(pluginId)),
  }
}
